import { Component, h, State, Prop, Method } from '@stencil/core';
import '../components/theme-provider';
import '../components/my-component/web-chat/WebChat';
import '../components/Login';

@Component({
  tag: 'chat-app',
  shadow: false
})
export class ChatApp {
  @Prop() themePath: string;
  @Prop({ mutable: true, reflect: true }) mode: 'light' | 'dark' = 'light';
  @Prop() themeObject: { [key: string]: string };
  
  @State() isLoggedIn: boolean = false;
  @State() userToken: string = null;
  @State() userId: string = null;
  
  // Reference to the theme provider element
  private themeProviderRef?: HTMLThemeProviderElement;
  
  // Bind methods in constructor to preserve 'this' context
  constructor() {
    this.toggleTheme = this.toggleTheme.bind(this);
    this.changeTheme = this.changeTheme.bind(this);
  }
  
  componentWillLoad() {
    // Load saved theme preference from localStorage
    const savedTheme = localStorage.getItem('preferred-theme');
    if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
      this.mode = savedTheme;
    }
    
    // Check for existing login session
    const userToken = this.getCookie('userToken');
    const userId = this.getCookie('isometrikUserId');
    
    if (userToken && userId) {
      this.isLoggedIn = true;
      this.userToken = userToken;
      this.userId = userId;
    }
  }
  
  private getCookie(name: string): string {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }
  
  private handleLoginSuccess = (event: CustomEvent) => {
    this.userToken = event.detail;
    this.isLoggedIn = true;
    
    // Get the updated userId from cookie as it was set by login component
    this.userId = this.getCookie('isometrikUserId');
  }
  
  /**
   * Changes the theme and saves the preference to localStorage
   * @param newTheme The theme to change to ('light' or 'dark')
   */
  @Method()
  async changeTheme(newTheme: 'light' | 'dark') {
    // Update the component's mode property
    this.mode = newTheme;
    
    // Save preference to localStorage
    localStorage.setItem('preferred-theme', newTheme);
    
    // Update theme provider if available
    if (this.themeProviderRef) {
      await this.themeProviderRef.setTheme(newTheme);
    }
    
    return this.mode;
  }
  
  /**
   * Toggles between light and dark themes
   */
  @Method()
  async toggleTheme() {
    const newTheme = this.mode === 'light' ? 'dark' : 'light';
    return this.changeTheme(newTheme);
  }
  
  render() {
    return (
      <theme-provider 
        ref={(el) => this.themeProviderRef = el as HTMLThemeProviderElement}
        themePath={this.themePath} 
        mode={this.mode}
      >
        {!this.isLoggedIn ? (
          <login-page onLoginSuccess={this.handleLoginSuccess} />
        ) : (
          <web-chat
            toggleTheme={this.toggleTheme}
            defaultTheme={this.mode}
          />
        )}
      </theme-provider>
    );
  }
}